import { ConfigurableFocusTrapFactory } from "@angular/cdk/a11y";
import { DOCUMENT } from "@angular/common";
import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Inject,
	Input,
	OnInit,
	Optional,
	Output,
	ViewContainerRef,
} from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { createRxValue, fromRxValue } from "@app/_helpers/utils";
import { ReportsComponent } from "@app/pages/reports-page/reports.component";
import { SatPopoverComponent as SatPopover, SatPopoverAnchoringService } from "@ncstate/sat-popover";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Logger, ProjectsQuery, Task, TasksQuery, TasksService, UserSettingsQuery } from "@timeghost/web-api";
import { flow, xor } from "lodash-es";
import { BehaviorSubject, combineLatest, of } from "rxjs";
import { distinctUntilChanged, map, startWith, tap } from "rxjs/operators";
import { firstBy } from "thenby";

const log = new Logger("TaskFilterPopoverComponent");

@UntilDestroy()
@Component({
	selector: "tg-task-filter-popover",
	templateUrl: "./task-filter-popover.component.html",
	styleUrls: ["./task-filter-popover.component.scss"],
	providers: [{ provide: SatPopoverAnchoringService, useExisting: false }],
})
export class TaskFilterPopoverComponent extends SatPopover implements OnInit {
	constructor(
		private userSettingsQuery: UserSettingsQuery,
		private tasksQuery: TasksQuery,
		private projectsQuery: ProjectsQuery,
		private tasksService: TasksService,
		_focusTrapFactory: ConfigurableFocusTrapFactory,
		_anchoringService: SatPopoverAnchoringService,
		_viewContainerRef: ViewContainerRef,
		@Optional() @Inject(DOCUMENT) _document: any,
		private cdr: ChangeDetectorRef,
		@Optional()
		private reports: ReportsComponent
	) {
		super(
			_focusTrapFactory,
			_anchoringService,
			_viewContainerRef,
			"200ms cubic-bezier(0.25, 0.8, 0.25, 1)",
			_document
		);
	}
	search = new FormControl("", [Validators.required, Validators.minLength(2)]);
	readonly projects$ = createRxValue([]);
	get projects() {
		return this.projects$.value;
	}
	@Input()
	set projects(val: string[]) {
		this.projects$.value = val;
	}

	private _selectedItems = new BehaviorSubject<string[]>([]);
	readonly selectedItems$ = this._selectedItems.asObservable().pipe(distinctUntilChanged());
	filteredTasks: Task[] = [];

	get selectedItems() {
		return this._selectedItems.getValue();
	}
	@Input()
	set selectedItems(val: string[]) {
		const prev = this.selectedItems;
		this._selectedItems.next(val);

		if (xor(val, prev).length > 0) this.selectedItemsChange.emit(val);
	}
	@Output()
	selectedItemsChange = new EventEmitter(true);

	items$ = fromRxValue(
		combineLatest([
			this.reports ? this.reports.data$tasks.asObservable() : this.tasksQuery.selectAll(),
			this.reports
				? this.reports.projectFilter.asObservable()
				: of(<typeof this.reports.projectFilter.value>null),
			this.selectedItems$,
		]).pipe(
			map(([tasks, selectedProjects, selected]) => {
				if (!tasks?.length) return [];
				const selectedNotFound = selected?.filter((x) => !tasks.find((y) => y.id === x)) || [];
				if (selectedNotFound?.length > 0)
					this.selectedItems = this.selectedItems.filter((x) => !selectedNotFound.find((y) => y === x));
				return flow(
					(d: Task[]) =>
						selectedProjects?.length > 0
							? d.filter((y) => !y.project || selectedProjects.includes(y.project.id))
							: d,
					(d) =>
						d.map((x) => {
							return {
								...x,
								selected: !!selected?.find((u) => u === x.id),
							};
						})
				)(tasks as Task[]);
			}),
			tap((tasks) => log.debug("tasks", tasks))
		)
	);

	items$filtered = fromRxValue(
		combineLatest([
			this.search.valueChanges.pipe(startWith(this.search.value), distinctUntilChanged()),
			this.items$.asObservable(),
		]).pipe(
			map(([q, tasks]) => {
				const filteredTasks = flow(
					(d: Task[]) => {
						if (this.search.invalid) return d;
						return d.filter((x) => {
							return x.name.toLowerCase().indexOf(q.toLowerCase()) !== -1;
						});
					},
					(d) => d.sort(firstBy("selected", "desc").thenBy("name", "asc"))
				)(tasks as Task[]);
				this.filteredTasks = filteredTasks;
				return filteredTasks;
			})
		)
	);

	@Output()
	itemsClose = new EventEmitter(true);

	ngOnInit(): void {
		this.openAnimationStartAtScale = this.closeAnimationEndAtScale = 0.9375;
		this.hasBackdrop = true;
		this.yAlign = "below";
		this.xAlign = "center";
		this.closed.asObservable().subscribe(() => this.search.reset(""));
		this.closed
			.asObservable()
			.pipe(
				untilDestroyed(this),
				map(() => this.selectedItems),
				distinctUntilChanged()
			)
			.subscribe((val) => this.itemsClose.emit(val));
	}

	trackId(index: number, { id }: { id: string }) {
		return id;
	}

	getTaskRecordedTime(task: Task): number {
		const projectId = task?.project?.id;
		const project = projectId ? this.projectsQuery.getEntity(projectId) : null;
		const isBillableProject = !!project?.billable;
		if (!task?.timesRecorded) return 0;
		return (
			(task.timesRecorded.billable?.clamp(0, task.timesRecorded.billable) || 0) +
			(!isBillableProject ? task.timesRecorded.notBillable?.clamp(0, task.timesRecorded.notBillable) || 0 : 0)
		);
	}

	selectItem(id: string) {
		if (this.selectedItems.findIndex((x) => x === id) !== -1)
			this.selectedItems = this.selectedItems.filter((x) => x !== id);
		else this.selectedItems = [...this.selectedItems, id];
	}

	async selectAll() {
		this.selectedItems = this.items$filtered.value.map((task) => task.id);
	}

	selectNone() {
		this.selectedItems = [];
	}

	close() {
		super.close(this.selectedItems);
	}
}
